import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Filter, Download, TrendingUp } from 'lucide-react';
import apiService from '@/services/api';

interface Customer {
  id: string;
  name: string;
  company: string;
  email: string;
  orders: number;
  totalSpend: string;
  outstanding: string;
  status: 'ACTIVE' | 'PENDING';
  joined: string;
  avatar: string;
}

const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('Total Spend');
  const [loading, setLoading] = useState(false);
  const [customersData, setCustomersData] = useState<Customer[]>([]);
  const [stats, setStats] = useState({ total: 0, activeThisMonth: 0, newThisWeek: 0, outstandingBalance: 0 });
  const itemsPerPage = 20;

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const [listRes, statsRes] = await Promise.all([
          apiService.request('/admin/customers', 'GET'),
          apiService.request('/admin/customers/stats', 'GET'),
        ]);
        if (listRes.success && Array.isArray(listRes.data)) setCustomersData(listRes.data);
        if (statsRes.success && statsRes.data) setStats(statsRes.data);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };
    fetchCustomers();
  }, []);

  const handleExportCustomers = async () => {
    setLoading(true);
    try {
      const response = await apiService.request('/admin/customers/export', 'GET');
      if (response.success) {
        const url = window.URL.createObjectURL(new Blob([JSON.stringify(response.data)]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `customers-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        alert('✓ Customers exported successfully');
      } else {
        alert('Error: ' + (response.message || 'Failed to export'));
      }
    } catch (error: any) {
      console.error('Error exporting customers:', error);
      alert('Failed to export: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterCustomers = () => {
    // Open filter modal or navigate to filter page
    navigate('/admin/customers?filter=true');
  };

  const customers: Customer[] = customersData;

  const getAvatarColor = (index: number) => {
    const colors = ['bg-pink-600', 'bg-blue-600', 'bg-purple-600', 'bg-orange-600'];
    return colors[index % colors.length];
  };

  const totalCustomers = stats.total || customers.length;
  const totalPages = Math.ceil(customers.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedCustomers = customers.slice(startIdx, startIdx + itemsPerPage);

  return (
    <>
      <Helmet>
        <title>Customers - Virewire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-5xl font-bold text-gray-900 mb-2">Customers</h1>
            <p className="text-gray-600">Directory management and institutional performance tracking for the global marketplace ecosystem.</p>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            {/* Total Customers */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total Customers</p>
              <p className="text-4xl font-bold text-gray-900 mb-2">{totalCustomers.toLocaleString()}</p>
            </div>

            {/* Active This Month */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Active This Month</p>
              <p className="text-4xl font-bold text-gray-900 mb-3">{stats.activeThisMonth.toLocaleString()}</p>
              <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full w-3/4 bg-gradient-to-r from-teal-600 to-green-600 rounded-full"></div>
              </div>
            </div>

            {/* New This Week */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">New This Week</p>
              <p className="text-4xl font-bold text-gray-900 mb-2">{stats.newThisWeek.toLocaleString()}</p>
            </div>

            {/* Outstanding Balance */}
            <div className="bg-green-600 rounded-xl p-6 shadow-lg text-white">
              <p className="text-xs font-bold uppercase tracking-widest mb-2 opacity-90">Outstanding Balance</p>
              <p className="text-4xl font-bold mb-1">£{(stats.outstandingBalance || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between mb-6">
            <Button 
              onClick={handleFilterCustomers}
              disabled={loading}
              className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-4 py-2 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
              >
                <option>Total Spend</option>
                <option>Name</option>
                <option>Orders</option>
                <option>Date</option>
              </select>
              <Button 
                onClick={handleExportCustomers}
                disabled={loading}
                className="border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 font-bold text-sm px-4 py-2 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left">
                      <input type="checkbox" className="w-4 h-4 accent-teal-600 rounded cursor-pointer" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Customer</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Company</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Orders</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Total Spend</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Outstanding</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Joined</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedCustomers.length === 0 && (
                    <tr><td colSpan={10} className="px-6 py-8 text-center text-gray-500 text-sm">No customers found</td></tr>
                  )}
                  {displayedCustomers.map((customer: any, idx) => {
                    const name = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
                    const initials = name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
                    const status = (customer.status || 'ACTIVE').toUpperCase();
                    const joined = customer.joined_date || customer.joined || customer.created_at || '—';
                    return (
                    <tr key={customer.id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-6 py-4">
                        <input type="checkbox" className="w-4 h-4 accent-teal-600 rounded cursor-pointer" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${getAvatarColor(idx)} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
                            {initials}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{name}</p>
                            <p className="text-xs text-gray-600">ID: {customer.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700">{customer.company || '—'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700">{customer.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-gray-900">{customer.orders ?? '—'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-bold text-gray-900">{customer.totalSpend || customer.total_spend || '£0.00'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className={`text-sm font-bold ${(customer.outstanding_value || 0) > 0 ? 'text-red-600' : 'text-gray-600'}`}>{customer.outstanding || '£0.00'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${status === 'ACTIVE' ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                          <span className={`text-xs font-bold uppercase ${status === 'ACTIVE' ? 'text-green-600' : 'text-gray-600'}`}>
                            {status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-700">{typeof joined === 'string' && joined.includes('T') ? new Date(joined).toLocaleDateString('en-GB') : joined}</p>
                      </td>
                      <td className="px-6 py-4">
                        <button onClick={() => navigate(`/admin/customers/${customer.id}`)} className="text-teal-600 hover:text-teal-900 text-sm font-bold transition-colors">View</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {startIdx + 1}-{Math.min(startIdx + itemsPerPage, totalCustomers)} of {totalCustomers} customers
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 text-sm transition-colors"
                >
                  ‹
                </button>
                {currentPage === 1 && (
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-3 py-1 rounded font-bold text-sm bg-teal-600 text-white"
                  >
                    1
                  </button>
                )}
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-3 py-1 border border-gray-300 text-gray-600 rounded text-sm hover:bg-gray-50 transition-colors"
                  >
                    1
                  </button>
                )}
                {currentPage > 2 && <span className="text-gray-600">...</span>}
                {currentPage > 1 && currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage)}
                    className="px-3 py-1 rounded font-bold text-sm bg-teal-600 text-white"
                  >
                    {currentPage}
                  </button>
                )}
                {currentPage < totalPages - 1 && <span className="text-gray-600">...</span>}
                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-1 border border-gray-300 text-gray-600 rounded text-sm hover:bg-gray-50 transition-colors"
                  >
                    {totalPages}
                  </button>
                )}
                <button 
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  className="px-3 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 text-sm transition-colors"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminCustomersPage;
